import React from 'react';
import styled, { css } from 'styled-components';
import { media } from '../theme/tokens';

interface MetricCardProps {
  label: string;
  value: string | number;
  unit?: string;
  delta?: { value: string; direction: 'up' | 'down' | 'flat' };
  hero?: boolean;
  unavailable?: boolean;
  onClick?: () => void;
}

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  unit,
  delta,
  hero = false,
  unavailable = false,
  onClick
}) => {
  return (
    <Card $hero={hero} $unavailable={unavailable} onClick={onClick}>
      {hero && <HeroGlow />}
      <Label $hero={hero}>{label}</Label>
      <Value $hero={hero}>
        {unavailable ? '—' : value}
        {unit && !unavailable && <Unit $hero={hero}>{unit}</Unit>}
      </Value>
      {delta && !unavailable && (
        <Delta $direction={delta.direction}>
          {delta.direction === 'up'
            ? '↑'
            : delta.direction === 'down'
              ? '↓'
              : '→'}{' '}
          {delta.value}
        </Delta>
      )}
      {unavailable && <UnavailableLabel>Data not available</UnavailableLabel>}
    </Card>
  );
};

const Card = styled.div<{ $hero: boolean; $unavailable: boolean }>`
  background: ${({ $hero }) =>
    $hero
      ? 'linear-gradient(135deg, var(--ink) 0%, #2a2620 100%)'
      : 'var(--white)'};
  color: ${({ $hero }) => ($hero ? 'var(--paper)' : 'var(--ink)')};
  border: ${({ $hero }) => ($hero ? 'none' : '1px solid var(--line)')};
  border-radius: var(--radius-lg);
  padding: 16px 18px;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  overflow: hidden;
  min-width: 0;

  ${({ $unavailable }) =>
    $unavailable &&
    css`
      opacity: 0.5;
      filter: grayscale(0.6);
      cursor: default;
    `}

  &:hover {
    box-shadow: ${({ $unavailable }) =>
      $unavailable ? 'none' : 'var(--shadow-md)'};
    border-color: ${({ $unavailable }) =>
      $unavailable ? 'var(--line)' : 'var(--ink-faint)'};
  }

  ${media.md} {
    padding: 20px 22px;
  }
`;

const HeroGlow = styled.div`
  position: absolute;
  top: -80px;
  right: -60px;
  width: 220px;
  height: 220px;
  background: radial-gradient(circle, var(--terracotta) 0%, transparent 70%);
  opacity: 0.3;
  pointer-events: none;
`;

const Label = styled.div<{ $hero: boolean }>`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: ${({ $hero }) =>
    $hero ? 'rgba(248, 245, 240, 0.55)' : 'var(--ink-faint)'};
  font-weight: 600;
  margin-bottom: 10px;
`;

const Value = styled.div<{ $hero: boolean }>`
  font-family: var(--font-sans-bold);
  font-size: ${({ $hero }) => ($hero ? '36px' : '28px')};
  font-weight: 500;
  letter-spacing: -0.025em;
  line-height: 1;
  margin-bottom: 8px;

  ${media.md} {
    font-size: ${({ $hero }) => ($hero ? '56px' : '38px')};
  }
`;

const Unit = styled.span<{ $hero: boolean }>`
  font-size: 14px;
  color: ${({ $hero }) =>
    $hero ? 'rgba(248, 245, 240, 0.55)' : 'var(--ink-faint)'};
  font-family: var(--font-sans);
  margin-left: 2px;

  ${media.md} {
    font-size: ${({ $hero }) => ($hero ? '18px' : '16px')};
  }
`;

const Delta = styled.span<{ $direction: 'up' | 'down' | 'flat' }>`
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: ${({ $direction }) =>
    $direction === 'up'
      ? 'var(--olive)'
      : $direction === 'down'
        ? 'var(--crimson)'
        : 'var(--ink-faint)'};
`;

const UnavailableLabel = styled.span`
  font-size: 11px;
  color: var(--ink-faint);
  font-style: italic;
`;
